import { Core } from "@faire/design-tokens";
import { trackRetailerPortalScrollPopularSearchesPage } from "@faire/web-api/events/retailerPortal/actionUnknown/scrollPopularSearchesPage";
import { IGetPopularSearchesResponse } from "@faire/web-api/indigofair/seo/IGetPopularSearchesResponse";
import { trackEventOnClick } from "@faire/web/common/trackEvent";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { getSpacing, GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useIntersectionObserver } from "@faire/web/ui/hooks/useIntersectionObserver";
import capitalize from "lodash/capitalize";
import { useState, useEffect, useMemo } from "react";
import * as React from "react";
import styled from "styled-components";

import { RetailerFooter } from "@faire/retailer/components/Footer/RetailerFooter";
import { FaireHelmet } from "@faire/retailer/components/Helmet";
import { StackedPillsWithTitle } from "@faire/retailer/components/LoggedOutPills/StackedPillsWithTitle";

export const NUM_OF_SEARCH_PILLS_TO_FETCH = 2000;
const BATCH_SIZE = 100;

interface IProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  clickEventPrefix: string;
  promise: PromiseLike<IGetPopularSearchesResponse>;
  helmetData: { title: string; description: string };
  linkTo: (search: string) => string;
}

export const MostPopularSearches: React.FunctionComponent<IProps> = ({
  promise,
  linkTo,
  clickEventPrefix,
  title,
  subtitle,
  helmetData,
}) => {
  const [numOfPillsToDisplay, setNumOfPillsToDisplay] = useState<number>(
    isPrerender() ? NUM_OF_SEARCH_PILLS_TO_FETCH : BATCH_SIZE
  );

  const [popularSearchesResponse, setPopularSearchesResponse] =
    useState<IGetPopularSearchesResponse>();

  useEffect(() => {
    const fetchData = async () => {
      const response = await promise;
      setPopularSearchesResponse(response);
    };
    fetchData();
  }, [popularSearchesResponse, promise]);

  const pills = useMemo(() => {
    return popularSearchesResponse?.queries
      ?.slice(0, numOfPillsToDisplay)
      .map((pillString) => {
        return {
          pillContent: capitalize(pillString),
          linkTo: linkTo(pillString),
          onClick: trackEventOnClick(`${clickEventPrefix}/${pillString}`),
        };
      });
  }, [
    clickEventPrefix,
    linkTo,
    numOfPillsToDisplay,
    popularSearchesResponse?.queries,
  ]);

  const canDisplayMorePills = useMemo(() => {
    return (
      (popularSearchesResponse?.queries?.length ?? 0) > numOfPillsToDisplay
    );
  }, [numOfPillsToDisplay, popularSearchesResponse?.queries?.length]);

  const displayMorePills = () => {
    if (canDisplayMorePills) {
      setNumOfPillsToDisplay(numOfPillsToDisplay + BATCH_SIZE);
      trackRetailerPortalScrollPopularSearchesPage();
    }
  };

  const viewMoreRef = useIntersectionObserver((entry) => {
    if (entry.isIntersecting) {
      displayMorePills();
    }
  });

  return (
    <Wrapper>
      <FaireHelmet
        title={helmetData.title}
        description={helmetData.description}
      />
      <Spacer height={{ mobileAndAbove: "4x", tabletAndAbove: "8x" }} />

      {/* title needs to be h1 for SEO purpose. please don't change this before speaking with Retailer SEO team */}
      <Typography
        variant={{
          mobileAndAbove: "displayMSerifRegular",
          tabletAndAbove: "displayLSerifRegular",
        }}
        as="h1"
      >
        {title}
      </Typography>
      <Spacer height={{ mobileAndAbove: 0, tabletAndAbove: "1x" }} />
      <Typography
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          tabletAndAbove: "subheadingSansRegular",
        }}
        color={Core.text.subdued}
      >
        {subtitle}
      </Typography>
      <Spacer height={{ mobileAndAbove: "5x", tabletAndAbove: "7x" }} />
      {pills?.length ? <StackedPillsWithTitle pills={pills} /> : null}
      {canDisplayMorePills ? (
        <span ref={viewMoreRef}>
          <StyledLoadingSpinner size="large" />
        </span>
      ) : null}
      <GrowingSpacer base="5x" />
      <RetailerFooter hideReferral />
    </Wrapper>
  );
};

const Wrapper = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
    text-align: left;
  }
  text-align: center;
  margin: auto;
  max-width: 960px;
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
  align-self: center;
`;
